import React, { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { PurchaseOrder, ReceivePOLineItemPayload } from '../../../types/inventory';
import { useEscapeKey } from '../../../hooks/useEscapeKey';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  onReceive: (payload: ReceivePOLineItemPayload) => Promise<void>;
};

export const ReceivePOModal: React.FC<Props> = ({ isOpen, onClose, purchaseOrder, onReceive }) => {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const lineItems = purchaseOrder.line_items || [];
  const receivableItems = lineItems.filter((li) => (li.quantity_ordered - li.quantity_received - li.quantity_cancelled) > 0);

  const [lineItemId, setLineItemId] = useState<string>(receivableItems[0]?.id || '');
  const [quantityReceived, setQuantityReceived] = useState<number>(1);
  const [lotNumber, setLotNumber] = useState<string>('');
  const [expirationDate, setExpirationDate] = useState<string>('');
  const [unitCost, setUnitCost] = useState<string>('0.00');
  const [receivedDate, setReceivedDate] = useState<string>(today);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEscapeKey(onClose, isOpen && !saving);

  if (!isOpen) return null;

  const selected = receivableItems.find((x) => x.id === lineItemId) || receivableItems[0];
  const remaining = selected ? (selected.quantity_ordered - selected.quantity_received - selected.quantity_cancelled) : 0;

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      if (!lineItemId) throw new Error('Select a line item');
      if (!lotNumber.trim()) throw new Error('Lot number is required');
      if (quantityReceived <= 0) throw new Error('Quantity must be > 0');
      if (quantityReceived > remaining) throw new Error(`Quantity exceeds remaining (${remaining})`);
      if (!unitCost) throw new Error('Unit cost is required');

      const payload: ReceivePOLineItemPayload = {
        line_item_id: lineItemId,
        quantity_received: quantityReceived,
        lot_number: lotNumber.trim(),
        unit_cost_received: unitCost,
        received_date: receivedDate || null,
        expiration_date: expirationDate || null,
      };
      await onReceive(payload);
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Failed to receive line item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/20 dark:bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-xl bg-white dark:bg-dark-bg-secondary rounded-2xl shadow-xl border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-dark-border flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary">Receive Purchase Order</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-gray-500 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg-hover"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && <div className="text-sm text-red-600">{error}</div>}

          <div className="text-sm text-gray-700 dark:text-dark-text-secondary">
            <div className="font-medium text-gray-900 dark:text-dark-text-primary">{purchaseOrder.po_number}</div>
            <div className="text-gray-500 dark:text-dark-text-muted">Status: {purchaseOrder.status}</div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Line Item</label>
            <select
              value={lineItemId}
              onChange={(e) => setLineItemId(e.target.value)}
              className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">Select…</option>
              {receivableItems.map((li) => {
                const rem = li.quantity_ordered - li.quantity_received - li.quantity_cancelled;
                return (
                  <option key={li.id} value={li.id}>
                    #{li.line_number} • {li.description} (remaining {rem})
                  </option>
                );
              })}
            </select>
            {selected?.catalog_item_id ? (
              <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">
                Linked: {selected.catalog_type}:{selected.catalog_item_id}
              </div>
            ) : (
              <div className="text-xs text-amber-700 mt-1">
                This line item is not linked to a catalog item; receiving will fail until it is linked.
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Quantity Received</label>
              <input
                type="number"
                min={1}
                value={quantityReceived}
                onChange={(e) => setQuantityReceived(Number(e.target.value))}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
              <div className="text-xs text-gray-500 dark:text-dark-text-muted mt-1">Remaining: {remaining}</div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Unit Cost Received</label>
              <input
                value={unitCost}
                onChange={(e) => setUnitCost(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="89.50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Lot #</label>
              <input
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="LOT2025A123"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Expiration Date</label>
              <input
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-secondary mb-1">Received Date</label>
              <input
                type="date"
                value={receivedDate}
                onChange={(e) => setReceivedDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 dark:border-dark-border bg-white dark:bg-dark-bg-tertiary text-gray-900 dark:text-dark-text-primary shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-dark-border flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-dark-border rounded-md text-sm font-medium text-gray-700 dark:text-dark-text-secondary bg-white dark:bg-dark-bg-tertiary hover:bg-gray-50 dark:hover:bg-dark-bg-hover"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-60"
            disabled={saving}
          >
            {saving ? 'Receiving…' : 'Receive'}
          </button>
        </div>
      </div>
    </div>
  );
};


